import { Inject, Injectable, DOCUMENT, OnDestroy } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset, usePreset } from '@primeng/themes';
import { environment } from '@environments/environment';
import { ThemeMode } from './types/theme-mode.type';
import { getThemePalette } from './constants/all-themes.constant';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private readonly THEME_MODE_STORAGE_KEY = 'theme-mode';
  private readonly LIGHT_THEME_STORAGE_KEY = 'light-theme';
  private readonly DARK_THEME_STORAGE_KEY = 'dark-theme';

  private defaultLightTheme = getThemePalette(environment.defaultThemeName, false);
  private defaultDarkTheme = getThemePalette(environment.defaultThemeName, true);
  private selectedLightTheme: any;
  private selectedDarkTheme: any;
  private currentThemeMode: ThemeMode = 'system';
  private mediaQuery: MediaQueryList;
  private readonly onSystemChange = (): void => {
    if (this.currentThemeMode === 'system') this.applyThemeMode();
  };
  private readonly onStorageChange = (event: StorageEvent): void => {
    if (event.key === this.THEME_MODE_STORAGE_KEY || event.key === null) {
      this.currentThemeMode = this.readThemeMode();
      this.applyThemeMode();
    }
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private primeng: PrimeNG,
  ) {
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.initializeThemes();
    this.mediaQuery.addEventListener('change', this.onSystemChange);
    window.addEventListener('storage', this.onStorageChange);
  }

  ngOnDestroy(): void {
    this.mediaQuery.removeEventListener('change', this.onSystemChange);
    window.removeEventListener('storage', this.onStorageChange);
  }

  private initializeThemes(): void {
    const savedLightTheme = this.readStorage(this.LIGHT_THEME_STORAGE_KEY);
    const savedDarkTheme = this.readStorage(this.DARK_THEME_STORAGE_KEY);

    this.selectedLightTheme = getThemePalette(savedLightTheme, false) || this.defaultLightTheme;
    this.selectedDarkTheme = getThemePalette(savedDarkTheme, true) || this.defaultDarkTheme;

    this.currentThemeMode = this.readThemeMode();

    const themePreset = this.createThemePreset(this.selectedLightTheme, this.selectedDarkTheme);
    this.primeng.theme.set({
      preset: themePreset,
      options: {
        prefix: 'p',
        darkModeSelector: '.dark-mode',
        cssLayer: false,
      },
    });

    this.applyThemeMode();
  }

  public setThemeMode(mode: ThemeMode): void {
    this.currentThemeMode = mode;
    this.writeStorage(this.THEME_MODE_STORAGE_KEY, mode);
    this.applyThemeMode();
  }

  public getThemeMode(): ThemeMode {
    return this.currentThemeMode;
  }

  private applyThemeMode(): void {
    const isDark = this.currentThemeMode === 'dark' || (this.isSystemMode() && this.mediaQuery.matches);
    const root = this.document.documentElement;
    root.classList.toggle('dark-mode', isDark);
    root.dataset.themeMode = this.currentThemeMode;
    root.style.colorScheme = isDark ? 'dark' : 'light';
  }

  public setLightTheme(newLightThemeId: string): void {
    this.selectedLightTheme = getThemePalette(newLightThemeId, false) || this.defaultLightTheme;
    this.writeStorage(this.LIGHT_THEME_STORAGE_KEY, newLightThemeId);
    usePreset(this.createThemePreset(this.selectedLightTheme, this.selectedDarkTheme));
  }

  public isDarkMode(): boolean {
    return this.document.documentElement.classList.contains('dark-mode');
  }

  public isSystemMode(): boolean {
    return this.currentThemeMode === 'system';
  }

  private createThemePreset(lightTheme: any, darkTheme: any): any {
    // Surface scales must stay ordered light-to-dark in both schemes. The legacy
    // custom dark palettes are reversed and are not valid PrimeNG surface scales.
    const surfaces = Aura.primitive.neutral;
    const darkStatusColors = {
      success: {
        background: 'var(--design-status-success-bg)',
        color: 'var(--design-status-success-ink)',
      },
      warn: {
        background: 'var(--design-status-warning-bg)',
        color: 'var(--design-status-warning-ink)',
      },
      danger: {
        background: 'var(--design-status-danger-bg)',
        color: 'var(--design-status-danger-ink)',
      },
    };
    const overlaySurface = {
      background: 'var(--design-surface-card)',
      borderColor: 'var(--design-hairline-strong)',
      color: 'var(--design-ink)',
    };
    const productSemantics = {
      overlay: { select: overlaySurface, popover: overlaySurface, modal: overlaySurface },
      primary: {
        color: 'var(--design-action)',
        inverseColor: 'var(--design-on-action)',
        hoverColor: 'var(--design-action-hover)',
        activeColor: 'var(--design-action-active)',
      },
      text: {
        color: 'var(--design-ink)',
        hoverColor: 'var(--design-ink)',
        mutedColor: 'var(--design-body)',
        hoverMutedColor: 'var(--design-ink)',
      },
      formField: {
        background: 'var(--design-surface-card)',
        color: 'var(--design-ink)',
        borderColor: 'var(--design-hairline-strong)',
        hoverBorderColor: 'var(--design-muted)',
        focusBorderColor: 'var(--design-focus)',
      },
      content: {
        background: 'var(--design-surface-card)',
        hoverBackground: 'var(--design-surface-hover)',
        borderColor: 'var(--design-hairline)',
        color: 'var(--design-ink)',
        hoverColor: 'var(--design-ink)',
      },
      highlight: {
        background: 'color-mix(in srgb, var(--design-primary) 14%, var(--design-surface-card))',
        focusBackground: 'color-mix(in srgb, var(--design-primary) 22%, var(--design-surface-card))',
        color: 'var(--design-ink)',
        focusColor: 'var(--design-ink)',
      },
    };
    return definePreset(Aura, {
      components: {
        tag: { colorScheme: { dark: darkStatusColors } },
        badge: { colorScheme: { dark: darkStatusColors } },
      },
      semantic: {
        primary: lightTheme,
        colorScheme: {
          light: {
            ...productSemantics,
            primary: { ...lightTheme, ...productSemantics.primary },
            surface: surfaces,
          },
          dark: {
            ...productSemantics,
            primary: { ...darkTheme, ...productSemantics.primary },
            surface: surfaces,
          },
        },
      },
    });
  }

  private readThemeMode(): ThemeMode {
    const saved = this.readStorage(this.THEME_MODE_STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Restricted storage must not prevent switching the current tab's theme.
    }
  }

  public getCurrentLightTheme(): string {
    const savedTheme = this.readStorage(this.LIGHT_THEME_STORAGE_KEY);
    return savedTheme || environment.defaultThemeName;
  }

  public getCurrentDarkTheme(): string {
    const savedTheme = this.readStorage(this.DARK_THEME_STORAGE_KEY);
    return savedTheme || environment.defaultThemeName;
  }
}
